/**
 * Catalog / list previews for a layout document.
 *
 * WebGL must run in the browser — Convex only stores the resulting R2 keys.
 * Produces four transparent PNGs: light/dark × thumb (retina) / preview.
 */
import { BACKGROUND, type RenderTheme } from "../render/theme"
import { getSceneHandle } from "../scene/sceneHandle"
import { useDesign } from "../state/designStore"
import { useEditor } from "../state/editorStore"
import { exportSquarePng, type ExportContext } from "./exportPng"

/** Fixed output sizes (CSS px @1x). Supersample renders at 2× for clean lines. */
export const LAYOUT_SNAPSHOT_SPECS = {
  /** ~160 CSS px cards at 2× DPR. */
  thumb: { size: 320, supersample: 2 },
  /** Detail / hover / share previews. */
  preview: { size: 1024, supersample: 2 },
} as const

export type LayoutSnapshotKind = keyof typeof LAYOUT_SNAPSHOT_SPECS

export type LayoutSnapshotBlobs = {
  thumbLight: Blob
  thumbDark: Blob
  previewLight: Blob
  previewDark: Blob
}

export type LayoutSnapshotSlot = keyof LayoutSnapshotBlobs

const THEMES: readonly RenderTheme[] = ["light", "dark"]

/**
 * Wait for React + R3F to apply theme/palette (shared materials + shading rig).
 * Double rAF after a macrotask is enough for useLayoutEffect + Part rematerialize.
 */
function settleThemeFrame(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve())
      })
    }, 0)
  })
}

function requireExportContext(): ExportContext {
  const handle = getSceneHandle()
  if (!handle) {
    throw new Error("El canvas del editor no está listo para capturar")
  }
  const view = useEditor.getState().view
  return {
    gl: handle.gl,
    scene: handle.scene,
    camera: handle.camera,
    view,
    aspect: handle.aspect,
  }
}

async function exportSize(
  context: ExportContext,
  kind: LayoutSnapshotKind,
): Promise<Blob> {
  const spec = LAYOUT_SNAPSHOT_SPECS[kind]
  return exportSquarePng(context, {
    size: spec.size,
    supersample: spec.supersample,
    framing: "fit",
    transparent: true,
    padding: 1.08,
  })
}

/**
 * Capture the four standard layout snapshots from the live editor scene.
 * Temporarily swaps illustration theme; restores the previous style afterward.
 *
 * Must run while the IsoCanvas is mounted (same session as the editor).
 */
export async function captureLayoutSnapshots(): Promise<LayoutSnapshotBlobs> {
  const setStyle = useDesign.getState().setStyle
  const previous = useDesign.getState().doc.style

  const result: Partial<LayoutSnapshotBlobs> = {}

  try {
    for (const theme of THEMES) {
      setStyle({ theme, background: BACKGROUND[theme] })
      await settleThemeFrame()

      const context = requireExportContext()
      const thumb = await exportSize(context, "thumb")
      const preview = await exportSize(context, "preview")

      if (theme === "light") {
        result.thumbLight = thumb
        result.previewLight = preview
      } else {
        result.thumbDark = thumb
        result.previewDark = preview
      }
    }
  } finally {
    setStyle({
      theme: previous.theme,
      background: previous.background,
      accent: previous.accent,
      outlineWidth: previous.outlineWidth,
      showOutlines: previous.showOutlines,
    })
    await settleThemeFrame()
  }

  if (
    !result.thumbLight ||
    !result.thumbDark ||
    !result.previewLight ||
    !result.previewDark
  ) {
    throw new Error("No se generaron todos los snapshots del layout")
  }

  return result as LayoutSnapshotBlobs
}

/** Stable filenames for local debugging / download; R2 keys are opaque. */
export function snapshotFileName(slot: LayoutSnapshotSlot, base = "layout"): string {
  return `${base}-${slot}.png`
}
